import { useEffect } from "react";
import { usePageBg } from "./PageBgContext";

type ContentWidth = "s" | "m" | "l" | "full";

interface Props {
	width?: ContentWidth;
	id?: string;
	children?: React.ReactNode;
	bg?: string;
}

/**
 * Per-page main content wrapper. The persistent sidebar/navbar lives in
 * {@link AppShell} so navigating between pages does not unmount it.
 *
 * The `bg` prop is also published to {@link PageBgContext} so AppShell can
 * paint the sidebar's behind-the-rounded-corner area in the same color and
 * avoid the white wedge that's otherwise visible on non-white pages.
 */
export const LoggedInLayout: React.FC<Props> = ({ id, width = "s", bg = "white", children }) => {
	const { setPageBg } = usePageBg();

	useEffect(() => {
		setPageBg(bg);
	}, [bg, setPageBg]);

	const widthClass = getWidthClass(width);
	const bgStyle = bg === "white" ? "bg-white" : { backgroundColor: bg };
	const bgClass = typeof bgStyle === "string" ? bgStyle : "";

	// The window scrolls, not <main>. Overflow is clipped rather than auto so
	// <main> isn't a scroll container that would trap `position: sticky`.
	return (
		<main
			id={id}
			className={`flex-1 min-w-0 ${bgClass} overflow-x-clip flex justify-center px-3 py-4 sm:px-4 lg:px-8`}
			style={typeof bgStyle === "object" ? bgStyle : undefined}
		>
			<div className={`w-full ${widthClass}`}>{children}</div>
		</main>
	);
};

function getWidthClass(width: ContentWidth): string {
	switch (width) {
		case "s":
			return "max-w-[800px]";
		case "m":
			return "max-w-[900px]";
		case "l":
			return "max-w-[1000px]";
		case "full":
			return "max-w-none";
		default:
			return "max-w-[900px]";
	}
}
